import { Checkbox } from '@rocco/components/checkbox';
import { cn } from '@rocco/utils/cn';
import {
    PUBLICATION_COVER_ASPECT,
    PublicationCovers,
} from '../config/publication-covers';

import type { DigitalPublicationOption } from '../api/get-digital-publication-options.server';

interface PublicationSelectionPaneProps {
    publications: DigitalPublicationOption[];
    /** HubSpot IDs currently ticked. */
    selected: string[];
    onToggle: (hubspotID: string) => void;
    /** Past the form the selection is committed, so the rows lose their controls. */
    readOnly?: boolean;
    /**
     * Tighter rows for the mobile sheet, where this sits above the form rather
     * than beside it — the desktop pane's scale pushes the heading and the first
     * field off the first screen.
     */
    compact?: boolean;
    /**
     * The catalogue has not answered yet. Distinct from an empty list: an empty
     * pane during the fetch reads as "there is nothing", which is a failure
     * message shown to a reader whose request is still in flight.
     */
    loading?: boolean;
    className?: string;
}

/**
 * The subscribe modal's left pane: every publication on offer, each with the
 * cover of its latest issue and what it is, tickable.
 *
 * Carries the selection rather than brand imagery — the reader is choosing here,
 * so what they are choosing stays in view beside the form. On mobile the pane is
 * hidden by TwoPaneModal, so the sheet renders this above the form instead; both
 * placements share this component.
 */
export const PublicationSelectionPane = ({
    publications,
    selected,
    onToggle,
    readOnly = false,
    compact = false,
    loading = false,
    className,
}: PublicationSelectionPaneProps) => {
    if (loading && publications.length === 0) {
        return (
            <ul
                className={cn(
                    'm-0 flex size-full list-none flex-col overflow-hidden bg-[#ebebeb]',
                    compact
                        ? 'gap-4 rounded-[0.5rem] px-4 py-4'
                        : 'gap-[1.96875rem] px-[1.3125rem] py-6',
                    className,
                )}
                aria-busy="true"
                aria-label="Loading publications"
            >
                {/* One row per publication we expect, so the pane holds its
                    height and the modal does not resize under the reader when
                    the real rows arrive. */}
                {[0, 1, 2, 3].map((row) => (
                    <li key={row} className="flex items-center">
                        <div
                            className={cn(
                                'shrink-0 animate-pulse rounded-[0.125rem] bg-black/8',
                                compact
                                    ? 'mr-3 h-[4.5rem] w-[3.6rem]'
                                    : 'mr-[0.8125rem] ml-[2.4375rem] h-[7.1875rem] w-[5.75rem]',
                            )}
                        />
                        <div className="min-w-0 flex-1">
                            <div className="h-4 w-2/5 animate-pulse rounded bg-black/8" />
                            <div className="mt-2 h-3 w-full animate-pulse rounded bg-black/5" />
                            <div className="mt-1.5 h-3 w-4/5 animate-pulse rounded bg-black/5" />
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    if (publications.length === 0) {
        return (
            <div
                className={cn(
                    'flex size-full items-center justify-center bg-[#ebebeb] p-6',
                    className,
                )}
            >
                <p className="m-0 text-center text-[0.9375rem] text-[#656565]">
                    Our publications are unavailable just now. Please try again
                    shortly.
                </p>
            </div>
        );
    }

    return (
        <ul
            className={cn(
                // Figma 3162:15486 — #ebebeb pane, rows on a 185.5px pitch
                // (154px cover + 31.5px gutter), checkbox column at x=21.
                //
                // Not centred: covers at their own ratio make the list taller
                // than the pane, and `justify-center` on a scroll container puts
                // the overflow above the scrollable area as well as below it —
                // the first publication loses its title off the top and no
                // amount of scrolling brings it back.
                'm-0 flex size-full list-none flex-col overflow-y-auto bg-[#ebebeb]',
                compact
                    ? 'gap-4 rounded-[0.5rem] px-4 py-4'
                    : 'gap-[1.96875rem] px-[1.3125rem] py-6',
                className,
            )}
        >
            {publications.map((publication) => {
                const isSelected = selected.includes(publication.hubspotID);
                const cover =
                    PublicationCovers[publication.newsletterType] ??
                    publication.coverImage;
                const row = (
                    <>
                        {readOnly ? null : (
                            <Checkbox
                                checked={isSelected}
                                onCheckedChange={() =>
                                    onToggle(publication.hubspotID)
                                }
                                aria-label={`Subscribe to ${publication.title}`}
                                className={cn(
                                    'size-[1.375rem] shrink-0 rounded-[0.25rem]',
                                    compact ? 'mr-3' : 'mr-[1.0625rem]',
                                )}
                                indicatorClassName="size-4"
                            />
                        )}
                        {cover ? (
                            /*
                             * The design's cover, already cropped to this box, so
                             * `cover` has nothing left to take off it. A series
                             * with no bundled artwork falls back to its latest
                             * issue, which is taller than the box and does lose
                             * its lower edge — better than an empty slot.
                             */
                            <img
                                src={cover}
                                alt=""
                                className={cn(
                                    PUBLICATION_COVER_ASPECT,
                                    'shrink-0 rounded-[0.125rem] object-cover',
                                    compact
                                        ? 'mr-3 w-[3.6rem]'
                                        : 'mr-[0.8125rem] w-[5.875rem]',
                                )}
                            />
                        ) : (
                            // Keeps the text column aligned when an issue has no
                            // cover, rather than letting one row hang left.
                            <div
                                className={cn(
                                    PUBLICATION_COVER_ASPECT,
                                    'shrink-0 rounded-[0.125rem] bg-[#e2e0dd]',
                                    compact
                                        ? 'mr-3 w-[3.6rem]'
                                        : 'mr-[0.8125rem] w-[5.875rem]',
                                )}
                            />
                        )}
                        <div className="min-w-0 flex-1">
                            <p
                                className={cn(
                                    'm-0 leading-[1.2] font-medium tracking-[-0.02em] text-[#181818]',
                                    compact
                                        ? 'text-[1rem]'
                                        : 'text-[1.1875rem]',
                                )}
                            >
                                {publication.title}
                            </p>
                            <p
                                className={cn(
                                    'm-0 mt-1 leading-[1.35] tracking-[-0.01em] text-[#4a4a4a]',
                                    compact
                                        ? 'line-clamp-2 text-[0.8125rem]'
                                        : // Three lines is what the design allows,
                                          // and what keeps every row on the same
                                          // pitch. The copy comes from HubSpot, so
                                          // it can be longer than the design's.
                                          'line-clamp-3 text-[0.9375rem]',
                                )}
                            >
                                {publication.description}
                            </p>
                        </div>
                    </>
                );

                return (
                    <li key={publication.hubspotID}>
                        {readOnly ? (
                            <div className="flex items-center">{row}</div>
                        ) : (
                            <label className="flex cursor-pointer items-center">
                                {row}
                            </label>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};
